import React, { useEffect, useMemo, useState } from "react";
import { wishlistApi } from "../services/api/wishlistApi";
import { productApi } from "../services/api/productApi";
import { ordersApi } from "../services/api/orderApi";
import { showError, showSuccess } from "../lib/toast";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import ImageWithLoader from "./ImageWithLoader";

interface WishlistProductItem {
  _id: string;
  product: {
    _id: string;
    name: string;
    price: number;
    image?: string;
    images?: string[];
    fileDetails?: {
      recommendedPrintSize?: string;
      resolution?: string;
    };
    artistId?: {
      name?: string;
      country?: string;
    };
  };
}

const WishlistDrawer: React.FC = () => {
  const { isLoggedIn } = useAuth();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<WishlistProductItem[]>([]);
  const [movingId, setMovingId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [selectAll, setSelectAll] = useState(false);
  const [policyAgreed, setPolicyAgreed] = useState(false);
  const [ownedProducts, setOwnedProducts] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  const loadWishlist = async () => {

    try {
      setLoading(true);
      const response: any = await wishlistApi.getWishlist();
      const rawItems = Array.isArray(response) ? response : response?.items || [];

      // Ensure product details are fully populated; if backend didn't populate,
      // fetch details per product and merge.
      const enriched: WishlistProductItem[] = await Promise.all(
        rawItems.map(async (it: any) => {
          const baseProduct = it?.product;
          if (baseProduct?.name && (baseProduct?.price !== undefined)) {
            return it;
          }
          try {
            const productId = baseProduct?._id || it?.productId || it?._id;
            if (!productId) return it;
            const pRes = await productApi.getProductById(productId);
            return { ...it, product: pRes.data } as WishlistProductItem;
          } catch {
            return it;
          }
        })
      );

      setItems(enriched as any);
      // Initialize selection map
      const sel: Record<string, boolean> = {};
      (enriched as any).forEach((it: any) => {
        if (it?.product?._id) sel[it.product._id] = false;
      });
      setSelected(sel);
      setSelectAll(false);

      // Check ownership for wishlist items
      if (isLoggedIn && enriched.length > 0) {
        const productIds = enriched.map(it => it?.product?._id).filter(Boolean);
        try {
          const ownershipResponse: any = await ordersApi.checkMultipleProductsOwnership(productIds);
          if (ownershipResponse.success && ownershipResponse.data?.ownershipMap) {
            const owned = new Set<string>();
            Object.entries(ownershipResponse.data.ownershipMap).forEach(([productId, isOwned]) => {
              if (isOwned) owned.add(productId);
            });
            setOwnedProducts(owned);
          }
        } catch (error) {
          console.error("Error checking owned products:", error);
        }
      }
    } catch (e: any) {
      console.error("Wishlist load error", e);
      showError(e?.message || "Failed to load wishlist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load once when component mounts; offcanvas can be opened any time
    // Only load if user is logged in
    if (isLoggedIn) {
      loadWishlist();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  // Listen for wishlist updates from other components
  useEffect(() => {
    const handleWishlistUpdate = () => {
      loadWishlist();
    };

    // Listen for custom wishlist update events
    window.addEventListener('wishlistUpdated', handleWishlistUpdate);

    return () => {
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const handleMoveToCart = async (productId: string) => {
    // Check if user already owns this product
    if (ownedProducts.has(productId)) {
      showError("You already own this product! Check your previous purchases.");
      navigate("/previous-purchase");
      return;
    }

    try {
      setMovingId(productId);
      await wishlistApi.moveToCart(productId, 1);
      showSuccess("Moved to cart");
      // Optimistically remove
      setItems(prev => prev.filter(it => it?.product?._id !== productId));
      setSelected(prev => {
        const copy = { ...prev };
        delete copy[productId];
        return copy;
      });
      navigate("/cart");
    } catch (e: any) {
      console.error("Move to cart error", e);
      const errorMsg = e?.response?.data?.message || e?.message || "Failed to move item to cart";
      showError(errorMsg);
      // If it's an ownership error, redirect to previous purchase
      if (errorMsg.includes("already own")) {
        setTimeout(() => navigate("/previous-purchase"), 1500);
      }
    } finally {
      setMovingId(null);
    }
  };

  const toggleSelect = (productId: string) => {
    setSelected(prev => {
      const next = { ...prev, [productId]: !prev[productId] };
      const allIds = Object.keys(next);
      setSelectAll(allIds.length > 0 && allIds.every(id => next[id]));
      return next;
    });
  };

  const toggleSelectAll = () => {
    const newVal = !selectAll;
    setSelectAll(newVal);
    setSelected(prev => {
      const next: Record<string, boolean> = {};
      Object.keys(prev).forEach(id => {
        next[id] = newVal;
      });
      return next;
    });
  };

  const selectedIds = useMemo(() => Object.keys(selected).filter(id => selected[id]), [selected]);

  const removeSelected = async () => {
    if (selectedIds.length === 0) return;
    try {
      await Promise.all(selectedIds.map(id => wishlistApi.removeFromWishlist(id)));
      setItems(prev => prev.filter(it => !selectedIds.includes(it?.product?._id)));
      setSelected(prev => {
        const next = { ...prev };
        selectedIds.forEach(id => delete next[id]);
        return next;
      });
      setSelectAll(false);
      showSuccess("Removed selected favorites");
    } catch (e: any) {
      console.error("Bulk remove error", e);
      showError(e?.message || "Failed to remove selected items");
    }
  };

  const buySelected = async () => {
    if (selectedIds.length === 0 || !policyAgreed) return;

    // Check if any selected products are already owned
    const ownedSelected = selectedIds.filter(id => ownedProducts.has(id));
    if (ownedSelected.length > 0) {
      const ownedNames = ownedSelected.map(id => {
        const item = items.find(it => it?.product?._id === id);
        return item?.product?.name || "Unknown";
      }).join(", ");
      showError(`You already own: ${ownedNames}. Please deselect them.`);
      return;
    }

    try {
      setMovingId("bulk");
      let successCount = 0;
      let failedProducts: string[] = [];

      for (const id of selectedIds) {
        try {
          // Move one by one to ensure backend consistency
          await wishlistApi.moveToCart(id, 1);
          successCount++;
          // Remove from local state
          setItems(prev => prev.filter(it => it?.product?._id !== id));
          setSelected(prev => {
            const copy = { ...prev };
            delete copy[id];
            return copy;
          });
        } catch (error: any) {
          // If it's an ownership error, track the product name
          const item = items.find(it => it?.product?._id === id);
          const productName = item?.product?.name || "Unknown";

          if (error?.response?.data?.message?.includes("already own")) {
            failedProducts.push(`${productName} (already owned)`);
          } else {
            failedProducts.push(productName);
          }
        }
      }

      if (successCount > 0) {
        showSuccess(`${successCount} item(s) added to cart`);
        if (failedProducts.length === 0) {
          navigate("/cart");
        }
      }

      if (failedProducts.length > 0) {
        showError(`Could not add: ${failedProducts.join(", ")}`);
      }
    } catch (e: any) {
      console.error("Bulk move error", e);
      showError(e?.message || "Failed to add selected items to cart");
    } finally {
      setMovingId(null);
    }
  };

  // Subtotal removed per design request

  return (
    <div
      className="offcanvas offcanvas-end wishlist_offcanvas"
      tabIndex={-1}
      id="wishlistDrawer"
      aria-labelledby="wishlistDrawerLabel"
      style={{ width: 560, background: "#FAF6EE" }}
    >
      <div className="offcanvas-header d-flex align-items-center justify-content-between" style={{ color: "#112C3B" }}>
        <h5 className="offcanvas-title m-0 font_one heading_text_2" id="wishlistDrawerLabel">Wishlist</h5>
        <button type="button" className="btn-close ms-2" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>
      <div className="d-flex align-items-center justify-content-end gap-2 me-3 mb-3">
        <span className="wishlist_selectAll fw_500 d-none d-sm-inline">Select All</span>
        <button
          type="button"
          className="btn"
          aria-label="Select all wishlist items"
          onClick={toggleSelectAll}
          style={{ width: 20, height: 20, borderRadius: 6, border: '1px solid rgba(17,44,59,0.16)', background: selectAll ? '#112C3B' : '#FFFFFF', color: selectAll ? '#FFFFFF' : 'transparent', padding: 0, lineHeight: 1 }}
        >
          ✓
        </button>
      </div>
      <div className="offcanvas-body" style={{ paddingTop: 0 }}>
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-dark" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading wishlist...</p>
          </div>
        ) : items.length === 0 ? (
          <p className="mt-2" style={{ color: "#112C3B" }}>Your wishlist is empty.</p>
        ) : (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {items.map((it) => {
              const product = it.product;
              const img = product?.image || product?.images?.[0] || "/assets/images/nav_img.png";
              return (
                <li key={product?._id} className="mb-2" style={{ borderRadius: 12, border: "1px solid rgba(17,44,59,0.12)", background: "#FFFFFF" }}>
                  <div className="d-flex align-items-center mb-2 p-2">
                    <a href={`/product/${product?._id}`} className="d-inline-block">
                      <ImageWithLoader
                        src={img}
                        alt={product?.name}
                        className="wishlist_img"
                      />
                    </a>
                    <div className="ms-3 flex-grow-1">
                      <a href={`/product/${product?._id}`} style={{ textDecoration: "none" }}>
                        <p className="m-0 fw_500 font_one wishlist_title" style={{ lineHeight: 1.25, color: "#112C3B" }}>{product?.name}</p>
                      </a>
                      <p className="m-0 mt-1 font_two fw_500 wishlist_artist" style={{ color: "#112C3B" }}>By {product?.artistId?.name || "Unknown"}{product?.artistId?.country ? `, ${product?.artistId?.country}` : ""}</p>
                      <div className="mt-1" style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                        <span className="fw_700 whishlist_price" >${(product?.price || 0).toFixed(2)}</span>
                      </div>
                      <div className="mt-1 font_two  wishlist_dimension" style={{ color: "#112C3B" }}>
                        <span className="fw_700">Dimensions:</span> <span className="fw_400">{product?.fileDetails?.recommendedPrintSize || product?.fileDetails?.resolution || "N/A"}</span>
                      </div>
                      <div className="mt-2 d-flex align-items-center gap-2 dplaynone">
                        {ownedProducts.has(product?._id) ? (
                          <button
                            className="blue_btn"
                            onClick={() => navigate("/previous-purchase")}
                            style={{ padding: "6px 16px", fontSize: 14, borderRadius: 20, background: "#10B981", borderColor: "#10B981" }}
                          >
                            ✓ Owned
                          </button>
                        ) : (
                          <button
                            className="blue_btn"
                            onClick={() => handleMoveToCart(product?._id)}
                            disabled={movingId === product?._id}
                            style={{ padding: "6px 16px", fontSize: 14, borderRadius: 20 }}
                          >
                            {movingId === product?._id ? "Processing..." : "Buy Now"}
                          </button>
                        )}
                        <button
                          type="button"
                          aria-label="favorite"
                          className="btn"
                          aria-disabled
                          style={{ width: 31, height: 31, borderRadius: 18, border: '1px solid #112C3B', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#FAF6EE', padding: 0, pointerEvents: 'none' }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18.1721 4.67022C14.5795 2.74344 12 5.58373 12 5.58373C12 5.58373 9.42042 2.74344 5.8279 4.67021C1.47665 7.00391 1.1642 16.1428 12 20.2C22.8359 16.1428 22.5233 7.00392 18.1721 4.67022Z" fill="#112C3B" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn d-flex align-items-center justify-content-center align-self-start"
                      onClick={() => toggleSelect(product?._id)}
                      aria-label={`Select ${product?.name}`}
                      style={{ width: 20, height: 20, borderRadius: 6, border: '1px solid rgba(17,44,59,0.16)', background: selected[product?._id] ? '#112C3B' : '#FFFFFF', color: selected[product?._id] ? '#FFFFFF' : 'transparent', padding: 0, lineHeight: 1 }}
                    >
                      ✓
                    </button>
                  </div>
                  <div className="whishlist_but_link_contain ">
                    <div className="mt-2 d-flex align-items-center justify-content-center p-2 gap-2 w-full">
                      {ownedProducts.has(product?._id) ? (
                        <button
                          className="blue_btn order-2"
                          onClick={() => navigate("/previous-purchase")}
                          style={{ padding: "6px 16px", fontSize: 14, borderRadius: 20, background: "#10B981", borderColor: "#10B981" }}
                        >
                          ✓ Owned
                        </button>
                      ) : (
                        <button
                          className="blue_btn order-2"
                          onClick={() => handleMoveToCart(product?._id)}
                          disabled={movingId === product?._id}
                          style={{ padding: "6px 16px", fontSize: 14, borderRadius: 20 }}
                        >
                          {movingId === product?._id ? "Processing..." : "Buy Now"}
                        </button>
                      )}
                      <button
                        type="button"
                        aria-label="favorite"
                        className="order-1"
                        aria-disabled
                        style={{ width: 46, height: 46, borderRadius: "50% ", border: '1px solid #112C3B', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#FAF6EE', padding: 0, pointerEvents: 'none' }}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M18.1721 4.67022C14.5795 2.74344 12 5.58373 12 5.58373C12 5.58373 9.42042 2.74344 5.8279 4.67021C1.47665 7.00391 1.1642 16.1428 12 20.2C22.8359 16.1428 22.5233 7.00392 18.1721 4.67022Z" fill="#112C3B" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      {items.length > 0 && (
        <>
          <div className="px-3 pt-2 pb-0">
            <div className="d-flex align-items-center mb-2">
              <button
                type="button"
                aria-checked={policyAgreed}
                role="checkbox"
                onClick={() => setPolicyAgreed(prev => !prev)}
                className="btn"
                style={{ width: 20, height: 20, borderRadius: 6, border: '1px solid rgba(17,44,59,0.16)', background: policyAgreed ? '#112C3B' : '#FFFFFF', color: policyAgreed ? '#FFFFFF' : 'transparent', padding: 0, lineHeight: 1, marginRight: 8 }}
              >
                ✓
              </button>
              <span
                onClick={() => setPolicyAgreed(prev => !prev)}
                className="wishlist_policy"
              >
                I Agree to DigiCartt’s No Refund / No Return Policy
              </span>
            </div>
          </div>
          <div className="px-3 pt-2 pb-3 d-flex gap-2">
            <button
              className="white_btn flex-grow-1 fw_500"
              onClick={removeSelected}
              disabled={selectedIds.length === 0}
            >
              Remove Favorites
            </button>
            <button
              className="blue_btn flex-grow-1"
              onClick={buySelected}
              disabled={selectedIds.length === 0 || !policyAgreed || movingId === "bulk"}
            >
              {movingId === "bulk" ? "Processing..." : "Buy Selected Items"}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default WishlistDrawer;


